import { cn } from "@theprelude/core";
import { createContext, forwardRef, useContext } from "react";
import { Button, ButtonProps } from "../button/button";

interface ToggleGroupProps<T> {
  value?: T;
  onValueChange?: (value: T) => void;
}

const ToggleGroupContext = createContext<ToggleGroupProps<any>>({});

export function ToggleGroup<T>({
  children,
  value,
  onValueChange,
}: {
  children: React.ReactNode;
  value: T;
  onValueChange: (value: T) => void;
}) {
  return (
    <div className="border-stroke-line bg-surface-background m-px flex h-9 w-fit flex-row gap-1 rounded-lg border p-0.5">
      <ToggleGroupContext.Provider
        value={{
          value,
          onValueChange: (value) => {
            onValueChange(value);
          },
        }}
      >
        {children}
      </ToggleGroupContext.Provider>
    </div>
  );
}

interface ToggleItemProps<T> extends Omit<ButtonProps, "value"> {
  icon?: React.ReactNode;
  value: T;
  count?: number;
}

function ToggleItemWithRef<T>(
  { value, children, count, icon, disabled, ...props }: ToggleItemProps<T>,
  ref: React.Ref<HTMLButtonElement>,
) {
  const { value: currentValue, onValueChange } = useContext(ToggleGroupContext);
  return (
    <Button
      ref={ref}
      type="button"
      icon={icon}
      className={cn(
        "text-secondary-foreground hover:bg-tertiary-background h-full rounded-md border-transparent bg-transparent px-3 py-1 font-normal hover:cursor-pointer hover:border-transparent hover:text-white hover:shadow-none dark:border-transparent dark:hover:border-solid",
        currentValue === value &&
          "text-primary-foreground bg-tertiary-foreground hover:bg-secondary-background hover:text-primary-foreground dark:hover:text-primary-foreground",
      )}
      onClick={() => onValueChange?.(value)}
      disabled={disabled}
      {...props}
    >
      {(children || count !== undefined) && (
        <div className="flex h-4 flex-row items-center justify-center gap-2">
          {children}

          {count !== undefined && (
            <div
              className={cn(
                "bg-secondary-background text-primary-foreground dark:text-primary-foreground inline-flex items-center justify-center rounded-2xl px-2 py-0 text-[0.625rem] font-normal",
                currentValue === value &&
                  "text-primary-background dark:text-primary-foreground bg-tertiary-background dark:bg-primary-background",
              )}
            >
              {count}
            </div>
          )}
        </div>
      )}
    </Button>
  );
}

export const ToggleItem = forwardRef(ToggleItemWithRef) as <T>(
  p: ToggleItemProps<T> & { ref?: React.Ref<HTMLButtonElement> },
) => React.ReactElement<T>;
